import type { CablePaths, CableNode, CircuitPathsState } from "./types";

export const ZOOM_OPACITY_THRESHOLD = 1.5;
export const OBJECT_BASE_SIZE = 40;
export const OBJECT_MAX_SIZE = 120;
// Multiplier for clickable/hoverable area (for user convenience)
export const OBJECT_INTERACTION_AREA_MULTIPLIER = 1.5;

// TODO: Add ctx.save()/ctx.restore() to all draw helpers.
export const getCableColorByType = (): string => {
  // Cable trays are always shown in thick blue
  return "#3b82f6";
};

export const parseNodeCoordinates = (
  node: CableNode,
): { x: number; y: number } => {
  return {
    x: parseFloat(node.x),
    y: parseFloat(node.y),
  };
};

export const findNodeById = (
  nodes: CableNode[],
  nodeId: number,
): CableNode | undefined => {
  return nodes.find((n) => n.id === nodeId);
};

export const drawCableEdge = (
  ctx: CanvasRenderingContext2D,
  fromNode: CableNode,
  toNode: CableNode,
  edgeType: string,
  lineWidth: number = 80,
  opacity: number = 0.7,
): void => {
  const { x: fromX, y: fromY } = parseNodeCoordinates(fromNode);
  const { x: toX, y: toY } = parseNodeCoordinates(toNode);

  // Cable trays are always blue
  const edgeColor = "#3b82f6";

  ctx.strokeStyle = edgeColor;
  ctx.lineWidth = lineWidth;
  ctx.globalAlpha = opacity;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(toX, toY);
  ctx.stroke();
};

export const drawCableNode = (
  ctx: CanvasRenderingContext2D,
  node: CableNode,
  radius: number = 15,
  fillOpacity: number = 0.3,
  strokeOpacity: number = 0.25,
): void => {
  const { x: nodeX, y: nodeY } = parseNodeCoordinates(node);

  ctx.fillStyle = "#3b82f6";
  ctx.globalAlpha = fillOpacity;

  ctx.beginPath();
  ctx.arc(nodeX, nodeY, radius, 0, 2 * Math.PI);
  ctx.fill();

  // Draw white border around node
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2;
  ctx.globalAlpha = strokeOpacity;
  ctx.stroke();
};

export const drawCablePaths = (
  ctx: CanvasRenderingContext2D,
  cablePaths: CablePaths,
  options: {
    lineOpacity?: number;
    showNodes?: boolean;
    nodeRadius?: number;
    zoomLevel?: number;
  } = {},
): void => {
  const {
    lineOpacity = 0.7,
    showNodes = false,
    nodeRadius = 15,
    zoomLevel = 1,
  } = options;

  // Simple linear inverse scaling: lineWidth = baseWidth / Math.max(zoomLevel, 0.1)
  const lineWidth = 16 / Math.max(zoomLevel, 0.1);

  // Draw edges (thick blue lines for cable trays)
  if (cablePaths.edges && cablePaths.edges.length > 0) {
    cablePaths.edges.forEach((edge) => {
      const fromNode = findNodeById(cablePaths.nodes, edge.fromNodeId);
      const toNode = findNodeById(cablePaths.nodes, edge.toNodeId);

      if (fromNode && toNode) {
        drawCableEdge(ctx, fromNode, toNode, edge.type, lineWidth, lineOpacity);
      }
    });

    ctx.globalAlpha = 1;
  }

  // Draw nodes (small circles at connection points) - optional
  if (showNodes && cablePaths.nodes && cablePaths.nodes.length > 0) {
    cablePaths.nodes.forEach((node) => {
      drawCableNode(ctx, node, nodeRadius * zoomLevel);
    });

    ctx.globalAlpha = 1;
  }
};

export const drawCableRoute = (
  ctx: CanvasRenderingContext2D,
  waypoints: Array<{ x: number; y: number }>,
  options: {
    color?: string;
    lineWidth?: number;
    opacity?: number;
    zoomLevel?: number;
  } = {},
): void => {
  if (!waypoints || waypoints.length < 2) return;

  const {
    color = "#ffff00",
    lineWidth: baseLineWidth = 8,
    opacity = 0.9,
    zoomLevel = 1,
  } = options;

  // Scale line width with zoom
  const lineWidth = baseLineWidth / Math.max(zoomLevel, 0.1);

  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.globalAlpha = opacity;

  ctx.beginPath();
  ctx.moveTo(waypoints[0].x, waypoints[0].y);

  for (let i = 1; i < waypoints.length; i++) {
    ctx.lineTo(waypoints[i].x, waypoints[i].y);
  }

  ctx.stroke();
  ctx.globalAlpha = 1;
};

export const getCircleSize = (zoomLevel: number) =>
  zoomLevel < 1
    ? Math.min(OBJECT_BASE_SIZE / zoomLevel, OBJECT_MAX_SIZE)
    : OBJECT_BASE_SIZE;

export const drawCircle = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  fillColor: string | null, // allow null
  strokeColor: string | null, // allow null
  lineWidth = 1,
  dash: number[] = [],
) => {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, 2 * Math.PI);

  if (fillColor) {
    ctx.fillStyle = fillColor;
    ctx.fill();
  }

  if (strokeColor) {
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = strokeColor;
    ctx.setLineDash(dash);
    ctx.stroke();
    ctx.setLineDash([]);
  }
};

export const drawLabeledCircle = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  fillColor: string,
  strokeColor: string,
  label: string,
  fontMultiplier = 0.6,
  zoomLevel = 1,
) => {
  const circleRadius = radius + 8;
  drawCircle(ctx, x, y, circleRadius, fillColor, strokeColor, 2 / zoomLevel);

  const fontSize = Math.max(
    radius + 8,
    Math.min(circleRadius * fontMultiplier, 36),
  );

  ctx.fillStyle = "#000000";
  ctx.strokeStyle = "transparent";
  ctx.lineWidth = fontSize * 0.15;
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.strokeText(label, x, y);
  ctx.fillText(label, x, y);
};

const withGlobalAlpha = (
  ctx: CanvasRenderingContext2D,
  alpha: number | undefined,
  draw: () => void,
) => {
  if (typeof alpha !== "number") {
    draw();
    return;
  }

  const previousAlpha = ctx.globalAlpha;
  ctx.globalAlpha = alpha;
  draw();
  ctx.globalAlpha = previousAlpha;
};

const DEST_LABEL_COLORS = {
  selected: { fill: "#22c55e", stroke: "#1f2937" },
  default: { fill: "#22c55e", stroke: "#22c55e" },
} as const;

export const drawDestinationMarker = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  isSelected: boolean,
  size: number,
  zoomLevel: number,
  opacity: number,
) => {
  const colors = isSelected
    ? DEST_LABEL_COLORS.selected
    : DEST_LABEL_COLORS.default;

  const markerRadius = isSelected ? size / 1.6 : size / 2;
  const circleRadius = markerRadius + 8;
  const labelText = opacity < 1 ? "" : "D";

  withGlobalAlpha(ctx, opacity, () => {
    drawLabeledCircle(
      ctx,
      x,
      y,
      markerRadius,
      colors.fill,
      "transparent",
      labelText,
      0.6,
      zoomLevel,
    );
  });

  drawCircle(ctx, x, y, circleRadius, null, colors.stroke, 2 / zoomLevel);
};

interface ObjectType {
  id: string;
  position: { x: number; y: number };
  rgb: [number, number, number];
  status?: string;
}

export const performDraw = (params: {
  ctx: CanvasRenderingContext2D;
  objects: ObjectType[];
  selectedObject?: ObjectType;
  zoomLevel: number;
  isFlashMode?: boolean;
  floorPlanCablePaths?: CablePaths;
  circuitPaths?: CircuitPathsState | null;
}) => {
  const {
    ctx,
    objects,
    selectedObject,
    zoomLevel,
    isFlashMode = false,
    floorPlanCablePaths,
    circuitPaths,
  } = params;

  ctx.save();

  const canvas = ctx.canvas;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const size = getCircleSize(zoomLevel);

  if (!(circuitPaths?.paths && circuitPaths.paths.length > 0)) {
    objects.forEach((obj) => {
      // When the object is selected, use its values
      const isSelected = selectedObject && selectedObject.id === obj.id;
      const currentElement = isSelected ? selectedObject : obj;
      const { x, y } = currentElement.position;
      const color = `rgb(${currentElement.rgb[0]}, ${currentElement.rgb[1]}, ${currentElement.rgb[2]})`;

      // Fill
      ctx.globalAlpha =
        (isFlashMode || zoomLevel < ZOOM_OPACITY_THRESHOLD) &&
        currentElement.status !== "uninstalled"
          ? 1
          : 0.2;
      drawCircle(ctx, x, y, isSelected ? size / 1.6 : size / 2, color, null);
      ctx.globalAlpha = 1;

      // Border
      if (isSelected) {
        drawCircle(ctx, x, y, size / 1.6, null, "#000000", 2 / zoomLevel);
      } else if (!(isFlashMode || zoomLevel < ZOOM_OPACITY_THRESHOLD)) {
        drawCircle(ctx, x, y, size / 2, null, color, 1);
      }
    });
  }

  // Draw floor plan cable tray network (blue lines)
  if (floorPlanCablePaths) {
    drawCablePaths(ctx, floorPlanCablePaths, {
      lineOpacity: 0.7,
      showNodes: false,
      zoomLevel,
    });
  }

  // Draw circuit paths (multiple yellow paths from distribution board to destinations)
  if (circuitPaths && circuitPaths.paths && circuitPaths.paths.length > 0) {
    const markerOpacity = zoomLevel < ZOOM_OPACITY_THRESHOLD ? 1 : 0.3;
    let selectedMarker: { x: number; y: number } | null = null;

    // Draw all cable routes
    for (const path of circuitPaths.paths) {
      if (!path.reachable || path.waypoints.length <= 0) continue;

      drawCableRoute(ctx, path.waypoints, {
        color: "#ffff00",
        opacity: 0.9,
        zoomLevel,
      });

      const lastWaypoint = path.waypoints[path.waypoints.length - 1];
      const isSelected = selectedObject?.id === path.destinationId;

      if (isSelected) {
        selectedMarker = { x: lastWaypoint.x, y: lastWaypoint.y };
        continue;
      }

      drawDestinationMarker(
        ctx,
        lastWaypoint.x,
        lastWaypoint.y,
        false,
        size,
        zoomLevel,
        markerOpacity,
      );
    }

    // Draw selected marker last so it’s always on top
    if (selectedMarker) {
      drawDestinationMarker(
        ctx,
        selectedMarker.x,
        selectedMarker.y,
        true,
        size,
        zoomLevel,
        markerOpacity,
      );
    }

    // Draw distribution board marker
    if (circuitPaths.distributionBoardPosition) {
      const { x, y } = circuitPaths.distributionBoardPosition;
      withGlobalAlpha(ctx, markerOpacity, () => {
        drawLabeledCircle(
          ctx,
          x,
          y,
          size / 2,
          "#eab308",
          "#eab308",
          "S",
          0.6,
          zoomLevel,
        );
      });
    }
  }

  ctx.restore();
};
